import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Button } from "@nextui-org/react";
import { faChevronDown } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { LayoutContext } from "../../contexts/LayoutContext.jsx";
import { sideNavLinks } from "../../constants/enum.js";
import SideNavItem from "./SideNavItem.jsx";

function SideNav() {

  const { showSideNav, setShowSideNav } = useContext(LayoutContext);

  const { menu } = useSelector((state) => state.auth);
  const { isMultipleCompany } = useSelector((state) => state.company);

  const navRef = useRef();
  const [searchItem, setSearchItem] = useState("");

  const [show, setShow] = useState({});
  const [showSub, setShowSub] = useState({});

  // ---------------- USER MENU ----------------
  const userMenuComponent = useMemo(() => {
    return menu?.userMenu?.map((item) =>
      item.controller !== null ? item.controller : item.menuname
    );
  }, [menu?.userMenu]);

  // ---------------- CLICK OUTSIDE ----------------
  const handleClickOutside = (event) => {
    if (navRef.current && !navRef.current.contains(event.target)) {
      setShowSideNav(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ---------------- MENU FILTER ----------------
  const filterMenu = (menuList, query) => {
    const lowerQuery = query.toLowerCase();

    const filterRecursive = (items) => {
      return items
        .map((item) => {
          const filteredSub = item?.sub
            ? filterRecursive(item.sub)
            : [];

          const isMatch =
            item?.name?.toLowerCase().includes(lowerQuery);

          if (isMatch || filteredSub.length > 0) {
            return {
              ...item,
              sub: filteredSub.length > 0 ? filteredSub : item.sub,
            };
          }

          return null;
        })
        .filter(Boolean);
    };

    return filterRecursive(menuList);
  };

  // ---------------- SEARCH MENU ----------------
  const SideNavMenu = useMemo(() => {
    if (searchItem) {
      return filterMenu(sideNavLinks, searchItem);
    }
    return sideNavLinks;
  }, [searchItem]);

  
  // ---------------- RENDER ----------------
  return (
    <div ref={navRef}>
      <aside
        id="logo-sidebar"
        className={`${!showSideNav ? "-translate-x-full" : ""} fixed top-0 left-0 z-40 w-60 h-screen pt-12 transition-transform border-t-2 border-gray-100 border-r border-gray-200 bg-sky-900`}
      >
        <div className="h-full shadow-[-1px_6px_12px_3px_rgba(0,_0,_0,_0.2)] bg-white overflow-y-auto">

          {/* SEARCH */}
          <div className="bg-slate-50 pb-2 border-0">
            <input
              type="text"
              placeholder="Search..."
              onChange={(e) => setSearchItem(e.target.value)}
              className="mt-2 mx-5 border border-gray-200 text-xs px-2 py-1"
            />
          </div>

          <div className="px-3 pb-4">
            <ul className="space-y-2 font-medium pt-2">

              {SideNavMenu.map((item, index) => {

                if (!item) return null;

                // ---------------- MENU WITH SUB ----------------
                if (item.sub) {

                  if (!userMenuComponent?.includes(item.key)) return null;

                  if (
                    isMultipleCompany &&
                    [
                      "Master",
                      "Transaction",
                      "OtherTransaction",
                      "SaleEntryMaster",
                      "PurchaseEntryMaster",
                      //"InterestCalculation",
                    ].includes(item.key)
                  ) {
                    return null;
                  }
 
                  return (
                    <li key={index}>
                      <Button
                        radius="none"
                        variant="light"
                        startContent={
                          item?.icon && (
                            <FontAwesomeIcon icon={item.icon} />
                          )
                        }
                        endContent={
                          <FontAwesomeIcon
                            className="text-xs"
                            icon={faChevronDown}
                          />
                        }
                        onPress={() =>
                          setShow((prev) => ({
                            ...prev,
                            [index]: !prev[index],
                          }))
                        }
                        className="h-7 text-sm w-full border-l-2 border-orange-600 flex justify-start text-sky-900 bg-white hover:bg-gradient-to-r from-sky-700 to-slate-50 hover:text-white"
                      >
                        {item.name}
                      </Button>

                      {/* SUB MENU */}
                      <ul className={`${show[index] ? "block" : "hidden"} py-1 space-y-1`}>

                        {item.sub.map((subItem, j) => {

                          if (!userMenuComponent?.includes(subItem.key)) return null;

                          if (
                            isMultipleCompany &&
                            (subItem.key === "GrayProductionEntry" || subItem.key === "FinalReports" || subItem.key=="InterestCalculation")
                          ) {
                            return null;
                          }

                          if (subItem.sub) {

                            return (
                              <li key={j}>
                                <Button
                                  radius="none"
                                  variant="light"
                                  startContent={
                                    subItem?.icon && (
                                      <FontAwesomeIcon
                                        className="text-red-700 text-sm"
                                        icon={subItem.icon}
                                      />
                                    )
                                  }
                                  endContent={<FontAwesomeIcon icon={faChevronDown} />}
                                  onPress={() =>
                                    setShowSub((prev) => ({
                                      ...prev,
                                      [j]: !prev[j],
                                    }))
                                  }
                                  className="ml-1 h-7 text-sm w-full bg-gradient-to-r from-red-100 to-slate-50 text-red-700 flex justify-start"
                                >
                                  {subItem.name}
                                </Button>

                                <ul className={`${showSub[j] ? "block" : "hidden"} py-1 space-y-2`}>

                                  {subItem.sub.map((nestItem, k) => {

                                    if (!userMenuComponent?.includes(nestItem.key)) return null;

                                    return (
                                      <SideNavItem
                                        key={k}
                                        navItem={nestItem}
                                        customeclass="h-7 text-gray-900 bg-orange-250 text-sm hover:bg-gradient-to-r from-gray-600 to-slate-50 hover:text-white"
                                      />
                                    );
                                  })}
                                </ul>
                              </li>
                            );
                          }

                          return (
                            <SideNavItem
                              key={j}
                              navItem={subItem}
                              customeclass="h-7 text-orange-600 bg-orange-250 hover:bg-gradient-to-r from-orange-600 to-slate-50 hover:text-white"
                            />
                          );
                        })}
                      </ul>
                    </li>
                  );
                }

                // ---------------- SIMPLE MENU ----------------
                if (userMenuComponent?.includes(item.key)) {

                  if (isMultipleCompany && item.key === "Ledger44") {
                    return null;
                  }

                  return (
                    <SideNavItem
                      key={index}
                      navItem={item}
                      customeclass=""
                    />
                  );
                }

                return null;
              })}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}

export default SideNav;